import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Missionary Furlough Support Raising: How to Reconnect With Donors During Home Assignment';
const DESCRIPTION =
    "A practical guide to using furlough (home assignment) to strengthen donor relationships, refill your support base, and close gaps that opened while you were on the field.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover28.jpg',
            datePublished: '2026-07-27',
            dateModified: '2026-07-27',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/missionary-furlough-support-raising',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'What is furlough or home assignment for a missionary?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Furlough, now more often called home assignment, is a period — typically weeks to a year — when a missionary returns to their home country to reconnect with supporting churches and donors, rest, and handle logistics before returning to the field.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should I ask for more support during home assignment?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes, if you have a real gap or rising field costs — home assignment is the natural, low-pressure moment to do it, since you're already meeting supporters face-to-face. Frame it as an honest update on your actual numbers rather than a generic re-ask.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How do I prevent donors from lapsing while I travel during home assignment?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Keep recurring donations running through a page that doesn't depend on you manually processing gifts, and send at least one substantive update mid-assignment even while you're busy traveling between churches. The donors most likely to lapse are the ones who hear nothing during a long home assignment.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How many churches or donors should I try to visit during furlough?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Prioritize depth over reach. A focused visit with your core sending churches and top donors, done well, does more for long-term retention than a rushed tour of every contact on your list. Reserve broader outreach for letters and calls instead of in-person visits.",
                    },
                },
            ],
        },
    ],
};

export default function MissionaryFurloughSupportRaising() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/missionary-furlough-support-raising' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-furlough-support-raising-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover28.jpg" alt="A church steeple against a bright sky, representing the sending churches missionaries reconnect with during home assignment" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Furlough — most sending agencies now call it home assignment — is often treated as a break from support raising. It's actually the opposite: it's the one stretch of your term when you can sit across a table from your donors instead of writing to them from another continent. Used well, it strengthens relationships and closes gaps that quietly opened while you were on the field. Used poorly, it becomes a blur of travel that leaves supporters feeling more distant, not less.</p>

                    <h2>Start with an honest audit, not a general re-ask</h2>

                    <p>Before you plan a single visit, pull your actual numbers: current monthly support, current field costs, and the gap between them. Support commitments erode over a multi-year term — some donors' circumstances change, some simply lapse without meaning to. Home assignment is the natural moment to close that gap, but the ask lands best as a specific, honest update ("here's where support actually stands today") rather than a vague appeal to give more.</p>

                    <h2>Prioritize depth over reach</h2>

                    <p>You will not see everyone, and trying to will burn your entire assignment on logistics instead of relationships. Rank your churches and top donors by relationship strength and giving level, then build your home-assignment calendar around the highest-priority visits first. A focused, unhurried visit with your core sending church does more for a ten-year relationship than a rushed five-minute greeting squeezed between two other stops. Reach the rest of your list through a well-written <a href="/resources/how-to-write-a-missionary-thank-you-letter">update or thank-you letter</a> instead of trying to physically visit everyone.</p>

                    <h2>Don't let recurring giving lapse while you travel</h2>

                    <p>The biggest quiet risk of home assignment isn't a bad visit — it's the donors who hear nothing for months because you're consumed by travel logistics. Make sure your recurring donations run on a system that doesn't depend on you processing anything manually, so gifts keep arriving whether or not you've had time to write that week. Send at least one substantive update partway through your assignment, even a short one, specifically to the donors you haven't managed to see in person — silence during a long home assignment is what causes lapses, not distance.</p>

                    <h2>Use in-person time for the conversation your letters can't have</h2>

                    <p>A letter is efficient, but home assignment gives you something a letter never can: a real conversation. Use visits to answer the questions donors have been sitting on, tell the stories that didn't fit in an update, and — critically — listen. Ask what they'd like to hear more about in future updates. This is also the best moment to <a href="/resources/how-to-ask-friends-and-family-to-support-your-mission-trip">ask close relationships for referrals</a>: people who've watched you serve for years are often glad to introduce you to someone new, and an in-person ask converts far better than one made from the field.</p>

                    <h2>Report back specifically, not generically</h2>

                    <p>Donors who've supported you for years want to know their giving mattered, not just that you were busy. Bring specific outcomes — a number reached, a story completed, a milestone hit — rather than a general "it was a great term." Specificity is what turns a polite thank-you visit into a donor who re-commits for another term without needing to be asked twice.</p>

                    <h2>Plan your return before you leave</h2>

                    <p>Before home assignment ends, send a clear update on your return timeline and any new or changed support needs for the next term. Donors who've just reconnected with you in person are primed to respond quickly — don't let that momentum sit unused for months after you're back on the field. If your gap isn't fully closed by the time you leave, keep the same weekly follow-up rhythm you'd use in any support-raising push; home assignment jumpstarts it, it doesn't have to finish it.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>What is furlough or home assignment for a missionary?</strong><br />
                    A period — weeks to a year — back in your home country to reconnect with supporting churches and donors, rest, and handle logistics before returning to the field.</p>

                    <p><strong>Should I ask for more support during home assignment?</strong><br />
                    Yes, if you have a real gap — it's the natural, low-pressure moment, framed as an honest update on your actual numbers rather than a generic re-ask.</p>

                    <p><strong>How do I prevent donors from lapsing while I travel during home assignment?</strong><br />
                    Keep recurring giving running on a page that doesn't need manual processing, and send at least one real update mid-assignment even while busy traveling.</p>

                    <p><strong>How many churches or donors should I try to visit during furlough?</strong><br />
                    Prioritize your core sending churches and top donors with focused visits; reach the rest through letters and calls instead of a rushed in-person tour.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-to-write-a-missionary-thank-you-letter">How to Write a Missionary Thank-You Letter Donors Actually Remember</a></li>
                        <li><a href="/resources/how-to-ask-your-church-to-support-you-as-a-missionary">How to Ask Your Church to Support You as a Missionary</a></li>
                        <li><a href="/resources/how-long-to-raise-full-support-as-a-missionary">How Long Does It Take to Raise Full Support as a Missionary?</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We wrote this after hearing the same pattern from missionaries again and again: home assignment either resets their support base for years, or quietly drains it — and the difference is almost always whether recurring giving kept running while they were on the road.</p>
                    <p><small><em>Last updated: 2026-07-27.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/missionary-furlough-support-raising" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
